/**
 * Tag Editor Component
 * Allows adding/removing tags from a document
 */

import React, { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { XMarkIcon, PlusIcon, TagIcon } from '@heroicons/react/24/outline'
import { documentsAPI } from '@api/documents'
import toast from 'react-hot-toast'
import clsx from 'clsx'

interface TagEditorProps {
  documentId: string
  currentTags: string[]
  onClose?: () => void
  className?: string
}

const TagEditor: React.FC<TagEditorProps> = ({
  documentId,
  currentTags,
  onClose,
  className,
}) => {
  const queryClient = useQueryClient()
  const [tags, setTags] = useState<string[]>(currentTags || [])
  const [newTag, setNewTag] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    setTags(currentTags || [])
  }, [currentTags])

  const updateTagsMutation = useMutation({
    mutationFn: async (updatedTags: string[]) => {
      return documentsAPI.update(documentId, { tags: updatedTags })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['documents', documentId] })
      setIsEditing(false)
      if (onClose) onClose()
    },
    onError: (error: any) => {
    },
  })

  const handleAddTag = () => {
    const tagName = newTag.trim()
    if (!tagName) return

    // Check for duplicates
    if (tags.includes(tagName)) {
      setNewTag('')
      return
    }

    const updatedTags = [...tags, tagName]
    setTags(updatedTags)
    setNewTag('')
    updateTagsMutation.mutate(updatedTags)
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToRemove)
    setTags(updatedTags)
    updateTagsMutation.mutate(updatedTags)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setNewTag('')
    }
  }

  if (!isEditing) {
    return (
      <div className={clsx('flex items-center gap-2 flex-wrap', className)}>
        {tags.length > 0 ? (
          tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md text-xs font-medium"
            >
              <TagIcon className="w-3 h-3" />
              {tag}
            </span>
          ))
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400">No tags</span>
        )}
        <button
          onClick={() => setIsEditing(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          title="Edit tags"
        >
          <PlusIcon className="w-3 h-3" />
          Add tag
        </button>
      </div>
    )
  }

  return (
    <div className={clsx('space-y-2', className)}>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md text-xs font-medium"
          >
            <TagIcon className="w-3 h-3" />
            {tag}
            <button
              onClick={() => handleRemoveTag(tag)}
              className="ml-1 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              disabled={updateTagsMutation.isPending}
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Enter tag name..."
          className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          autoFocus
          disabled={updateTagsMutation.isPending}
        />
        <button
          onClick={handleAddTag}
          disabled={updateTagsMutation.isPending || !newTag.trim()}
          className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
        >
          Add
        </button>
        <button
          onClick={() => {
            setIsEditing(false)
            setNewTag('')
            setTags(currentTags || [])
          }}
          disabled={updateTagsMutation.isPending}
          className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export default TagEditor

